import "bootstrap/dist/css/bootstrap.min.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Table } from "react-bootstrap";
import { FaCheck, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { useSortBy, useTable } from "react-table";

const BASE_URL = process.env.REACT_APP_API_URL;


const FactoryStructure = () => {
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [message, setMessage] = useState("");
  const [companyData, setCompanyData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const FactoryCode = userData?.FactoryCode;

  // React Table column definition
  const columns = useMemo(
    () => [
      {
        Header: "Branch",
        accessor: "branchName",
        Cell: ({ value }) => (
          <span className="fw-medium">{value}</span>
        ),
      },
        {
            Header: "Departments",
            accessor: "departments",
            Cell: ({ value }) => {
            const depts = Array.isArray(value) 
                ? value 
                : typeof value === 'string'
                ? value.split(',')
                : [];
            
            return (
                <div className="d-flex flex-wrap gap-1">
                {depts.map((dept, idx) => (
                    <Badge bg="info" key={idx} className="text-dark bg-info bg-opacity-25 px-2 py-1">
                    {dept}
                    </Badge>
                ))}
                </div>
            );
            },
        },
      {
        Header: "Actions",
        id: "actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2 justify-content-center">
            <Button 
              variant="outline-warning" 
              size="sm" 
              onClick={() => handleEdit(row.original.id)}
              disabled={isLoading}
              className="d-flex align-items-center"
            >
              <FaEdit className="me-1" /> Edit
            </Button>
            <Button 
              variant="outline-danger" 
              size="sm" 
              onClick={() => confirmDelete(row.original.id)}
              disabled={isLoading}
              className="d-flex align-items-center"
            >
              <FaTrash className="me-1" /> Delete
            </Button>
          </div>
        ),
      },
    ],
    [isLoading]
  );

  // Clear notification messages after delay
  useEffect(() => {
    if (message || errorMessage) {
      const timeout = setTimeout(() => {
        setMessage("");
        setErrorMessage("");
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [message, errorMessage]);

  // Fetch all branches
  const fetchBranches = useCallback(async () => {
    if (!FactoryCode) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/get-factory-branches?FactoryCode=${FactoryCode}`);
      if (!response.ok) {
        throw new Error("Failed to fetch branches");
      }
      const data = await response.json();
      setBranches(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setErrorMessage("Error fetching branches. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [FactoryCode]);

  // Fetch all departments
  const fetchDepartments = useCallback(async () => {
    if (!FactoryCode) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/get-factory-departments?FactoryCode=${FactoryCode}`);
      if (!response.ok) {
        throw new Error("Failed to fetch departments");
      }
      const data = await response.json();
      setDepartments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching departments:", error);
      setErrorMessage("Error fetching departments. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [FactoryCode]);

  // Fetch company data
  const fetchCompanyData = useCallback(async () => {
    if (!FactoryCode) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/get-factory-structure?FactoryCode=${FactoryCode}`);
      if (!response.ok) {
        throw new Error("Failed to fetch company structure data");
      }
      const data = await response.json();
      
      // Ensure we're working with an array and departments is correctly formatted
      const formattedData = Array.isArray(data) ? data.map(item => ({
        ...item,
        departments: Array.isArray(item.departments) 
          ? item.departments 
          : typeof item.departments === 'string' 
            ? item.departments.split(',').map(d => d.trim()) 
            : []
      })) : [];
      
      setCompanyData(formattedData);
    } catch (error) {
      console.error("Error fetching company data:", error);
      setErrorMessage("Error fetching company structure data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [FactoryCode]);

  // Initial data loading
  useEffect(() => {
    fetchBranches();
    fetchDepartments();
    fetchCompanyData();
  }, [fetchBranches, fetchDepartments, fetchCompanyData]);

  // Setup React Table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: companyData,
    },
    useSortBy
  );

  // Fixed handleDepartmentCheck to prevent duplicates
  const handleDepartmentCheck = (departmentName) => {
    setSelectedDepartments((prev) => {
      // If department is already in the array, remove it; otherwise add it
      // This ensures no duplicates can be added
      if (prev.includes(departmentName)) {
        return prev.filter((d) => d !== departmentName);
      } else {
        // Only add if it's not already in the array
        return [...prev, departmentName];
      }
    });
  };

  const selectAllDepartments = () => {
    // Only add departments that aren't already selected
    setSelectedDepartments(prev => {
      const uniqueDepts = new Set([...prev]);
      departments.forEach(d => uniqueDepts.add(d.departmentName));
      return Array.from(uniqueDepts);
    });
  };

  const clearAllDepartments = () => {
    setSelectedDepartments([]);
  };

  const handleSave = async () => {
    if (!selectedBranch || selectedDepartments.length === 0) {
      setErrorMessage("Please select a branch and at least one department.");
      return;
    }

    // Check for duplicate branch when adding new structure
    if (!isEditing) {
      const branchExists = companyData.some(
        item => item.branchName.toLowerCase() === selectedBranch.toLowerCase()
      );
      
      if (branchExists) {
        setErrorMessage("This branch already has a structure defined. Please select a different branch.");
        return;
      }
    }

    // Ensure no duplicate departments before saving
    const uniqueDepartments = [...new Set(selectedDepartments)];
    
    setIsLoading(true);
    const data = {
      branchName: selectedBranch,
      departments: uniqueDepartments, // Use unique departments array
      FactoryCode,
    };

    try {
      let response;
      if (isEditing && editId) {
        response = await fetch(`${BASE_URL}/update-factory-structure/${editId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
      } else {
        response = await fetch(`${BASE_URL}/create-factory-structure`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Operation failed");
      }

      const result = await response.json();
      setMessage(result.message || (isEditing ? "Company structure updated successfully!" : "Company structure saved successfully!"));
      resetForm();
      setShowModal(false);
      setIsEditing(false);
      await fetchCompanyData(); // Refresh the company data
    } catch (error) {
      console.error("Error saving data:", error);
      setErrorMessage(error.message || "Error saving data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedBranch("");
    setSelectedDepartments([]);
    setErrorMessage("");
  };

  const openCreateModal = () => {
    resetForm();
    setIsEditing(false);
    setEditId(null);
    setShowModal(true);
  };

  const handleEdit = (id) => {
    const dataToEdit = companyData.find((data) => data.id === id);
    if (dataToEdit) {
      setSelectedBranch(dataToEdit.branchName);
      
      // Ensure departments are properly formatted for editing
      const deptArray = Array.isArray(dataToEdit.departments) 
        ? dataToEdit.departments 
        : typeof dataToEdit.departments === 'string'
          ? dataToEdit.departments.split(',').map(d => d.trim())
          : [];
      
      // Ensure no duplicates in the departments array
      setSelectedDepartments([...new Set(deptArray)]);
      setIsEditing(true);
      setEditId(id);
      setShowModal(true);
    } else {
      setErrorMessage("Could not find the selected item.");
    }
  };

  const confirmDelete = (id) => {
    setItemToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) {
      setErrorMessage("Invalid data or missing ID.");
      setShowDeleteModal(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/delete-factory-structure/${itemToDelete}`, { method: "DELETE" });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete company structure");
      }

      const result = await response.json();
      setMessage(result.message || "Company structure deleted successfully!");
      await fetchCompanyData(); // Refresh the data instead of manually filtering
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting data:", error);
      setErrorMessage(error.message || "Error deleting data. Please try again.");
      setShowDeleteModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3 w-75">
      <Card className="shadow border-0 rounded-3">
        <Card.Header className="bg-primary text-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0 fs-4">Branch & Department Structure</h2>
            <Button 
              className="d-flex align-items-center" 
              variant="light" 
              onClick={openCreateModal}
              disabled={isLoading}
            >
              <FaPlus className="me-2" /> <span className="d-none d-sm-inline">Add</span>
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-3 p-sm-4">
          {/* Notifications */}
          {message && (
            <Alert variant="success" className="d-flex align-items-center mb-3">
              <FaCheck className="me-2" />
              <div>{message}</div>
            </Alert>
          )}
          {errorMessage && (
            <Alert variant="danger" className="mb-3">
              {errorMessage}
            </Alert>
          )}

          {/* Enhanced Loading indicator */}
          {isLoading && !showModal && !showDeleteModal && (
            <div className="text-center py-5">
              <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-primary fw-bold mb-1">Loading Structure Data</p>
              <p className="text-muted">Please wait while we fetch your company structure...</p>
            </div>
          )}

          {/* React Table - only show when not loading */}
          {!isLoading && (
            <>
              {companyData.length === 0 ? (
                <div className="text-center py-5 bg-light rounded">
                  <p className="mb-0 text-muted">
                    No company structure data found. Please add your first structure.
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
  <Table striped bordered hover {...getTableProps()}>
    <thead>
      {headerGroups.map(headerGroup => (
        <tr {...headerGroup.getHeaderGroupProps()}>
          {headerGroup.headers.map(column => (
            <th {...column.getHeaderProps(column.getSortByToggleProps())} className="bg-light">
              {column.render('Header')}
              <span className="ms-1">
                {column.isSorted
                  ? column.isSortedDesc
                    ? '🔽'
                    : '🔼'
                  : ''}
              </span>
            </th>
          ))}
        </tr>
      ))}
    </thead>
    <tbody {...getTableBodyProps()}>
      {rows.map(row => {
        prepareRow(row);
        return (
          <tr {...row.getRowProps()}>
            {row.cells.map(cell => {
              return (
                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  </Table>
</div>

              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal for adding/editing */}
      <Modal 
        show={showModal} 
        onHide={() => {
          setShowModal(false);
          resetForm();
        }}
        centered
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fs-5">
            {isEditing ? (
              <span>Edit Company Structure </span>
            ) : (
              "Add New Company Structure"
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
          <Form>
            <Form.Group controlId="branchSelect" className="mb-4">
              <Form.Label className="fw-medium">Select Branch</Form.Label>
              <Form.Select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                disabled={isLoading}
                className="border-secondary-subtle"
              >
                <option value="">-- Select Branch --</option>
                {branches.map((branch) => {
                  const isUsed = companyData.some(
                    item => item.branchName.toLowerCase() === branch.branchName.toLowerCase() && 
                    (!isEditing || item.id !== editId)
                  );
                  
                  return (
                    <option 
                      key={branch.id || branch.branchName} 
                      value={branch.branchName}
                      disabled={isUsed}
                    >
                      {branch.branchName} {isUsed && '(Already configured)'}
                    </option>
                  );
                })}
              </Form.Select>
              <Form.Text className="text-muted">
                Each branch can only have one structure configuration
              </Form.Text>
            </Form.Group>
            
            <Form.Group controlId="departmentSelect">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="fw-medium mb-0">Select Departments</Form.Label>
                <div>
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={selectAllDepartments}
                    disabled={isLoading || departments.length === 0}
                    className="text-decoration-none p-0 me-2"
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={clearAllDepartments}
                    disabled={isLoading || selectedDepartments.length === 0}
                    className="text-decoration-none text-danger p-0"
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              
              <div 
                className="border rounded p-3" 
                style={{ 
                  maxHeight: '250px', 
                  overflowY: 'auto',
                  backgroundColor: departments.length === 0 ? '#f8f9fa' : 'white' 
                }}
              >
                {departments.length === 0 ? (
                  <p className="text-muted mb-0 text-center py-3">No departments available</p>
                ) : (
                  <div className="row">
                    {departments.map((department) => (
                      <div className="col-md-4 mb-2" key={department.id || department.departmentName}>
                        <Form.Check
                          type="checkbox"
                          id={`dept-${department.id || department.departmentName}`}
                          label={department.departmentName}
                          checked={selectedDepartments.includes(department.departmentName)}
                          onChange={() => handleDepartmentCheck(department.departmentName)}
                          disabled={isLoading}
                          className="user-select-none"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {selectedDepartments.length > 0 && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="text-muted small">
                      Selected: <span className="fw-medium">{selectedDepartments.length}</span> department(s)
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedDepartments.map((dept) => (
                      <Badge 
                        bg="info" 
                        key={dept} 
                        className="text-dark bg-info bg-opacity-25 py-2 px-3 d-flex align-items-center"
                      >
                        {dept}
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="ms-1 p-0 text-danger" 
                          onClick={() => handleDepartmentCheck(dept)}
                          disabled={isLoading}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowModal(false);
              resetForm();
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            variant={isEditing ? "warning" : "primary"} 
            onClick={handleSave}
            disabled={isLoading || !selectedBranch || selectedDepartments.length === 0}
          >
            {isLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>{isEditing ? "Updating..." : "Saving..."}</span>
              </>
            ) : (
              isEditing ? "Update Structure" : "Save Structure"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)}
        centered
        backdrop="static"
        size="sm"
      >
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title className="fs-5">Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="mb-0">Are you sure you want to delete this company structure? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                <span>Deleting...</span>
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        /* Enhanced loading animation */
        .spinner-grow {
          animation-duration: 1.5s;
        }
        
        .text-primary.fw-bold {
          font-size: 1.2rem;
        }
        
        /* Fadeout animation for when loading completes */
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        .loading-fadeout {
          animation: fadeOut 0.5s ease-out forwards;
        }
        
        /* Table styles */
        .table th {
          font-weight: 600;
          background-color: #f8f9fa !important;
        }
        
        .badge {
          font-weight: 500;
        }
        
        /* Make table more responsive */
        @media (max-width: 992px) {
          .container-fluid {
            width: 100% !important;
          }
        }
        
        /* Slightly enlarge the sortable column indicators */
        th span {
          font-size: 0.8rem;
        }
      `}</style>
    </div>
  );
};

export default FactoryStructure;